/**
 * Diagnostic instrumentation for the sequencer onset clock-lock investigation.
 * Entirely gated behind ?onsetdiag=1: when off, every record call is a no-op and
 * nothing is allocated. It logs per contact-onset fire the frame-arrival ctx
 * time (`now`), the scheduled `fireTime`, and the contact window's sim time
 * (`windowSim`), plus the sequencer emit-crossing sim times. The analysis then
 * compares frame-bound onset-interval jitter against the sim-locked emission /
 * window-interval jitter and sizes the clock-anchor lag (now - windowSim) and
 * its drift — the numbers that set the T2 anchor method and latency budget L.
 */

export const ONSET_DIAG: boolean =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("onsetdiag") === "1";

interface FireSample {
  now: number;
  fire: number;
  sim: number;
  i: number;
}

const FIRE_CAP = 40000;
const EMIT_CAP = 40000;

const fires: FireSample[] = [];
const emits: number[] = [];

export function recordOnsetFire(now: number, fire: number, sim: number, i: number): void {
  if (fires.length >= FIRE_CAP) {
    return;
  }
  fires.push({ now, fire, sim, i });
}

export function recordSeqEmit(elapsed: number): void {
  if (emits.length >= EMIT_CAP) {
    return;
  }
  emits.push(elapsed);
}

function intervalStats(values: number[]): { n: number; meanMs: number; stdMs: number; maxAbsDevMs: number } {
  if (values.length < 2) {
    return { n: 0, meanMs: 0, stdMs: 0, maxAbsDevMs: 0 };
  }
  const deltas: number[] = [];
  for (let k = 1; k < values.length; k++) {
    deltas.push((values[k] - values[k - 1]) * 1000);
  }
  const mean = deltas.reduce((a: number, b: number): number => a + b, 0) / deltas.length;
  const variance = deltas.reduce((a: number, b: number): number => a + (b - mean) * (b - mean), 0) / deltas.length;
  let maxAbsDev = 0;
  for (const d of deltas) {
    const dev = Math.abs(d - mean);
    if (dev > maxAbsDev) {
      maxAbsDev = dev;
    }
  }
  return { n: deltas.length, meanMs: mean, stdMs: Math.sqrt(variance), maxAbsDevMs: maxAbsDev };
}

/**
 * Per-object onset jitter A/B: `now` is the frame-arrival schedule the current
 * code uses (the symptom); `sim` is the sim-locked window time the clock-anchor
 * fix would schedule from. A large now-std with a near-zero sim-std is the
 * frame-bound proof and the predicted post-fix floor (= the contact-window
 * substep quantization). Only objects with enough fires are reported.
 */
export function summarizeOnsetDiag(minFires: number = 8): unknown {
  const byObject = new Map<number, FireSample[]>();
  for (const f of fires) {
    const list = byObject.get(f.i);
    if (list === undefined) {
      byObject.set(f.i, [f]);
    } else {
      list.push(f);
    }
  }
  const perObject: unknown[] = [];
  for (const [i, list] of byObject) {
    if (list.length < minFires) {
      continue;
    }
    perObject.push({
      object: i,
      fires: list.length,
      nowInterval: intervalStats(list.map((f: FireSample): number => f.fire)),
      simInterval: intervalStats(list.map((f: FireSample): number => f.sim)),
    });
  }

  let lagSum = 0;
  let lagMax = -Infinity;
  let lagMin = Infinity;
  for (const f of fires) {
    const lag = (f.now - f.sim) * 1000;
    lagSum += lag;
    if (lag > lagMax) {
      lagMax = lag;
    }
    if (lag < lagMin) {
      lagMin = lag;
    }
  }
  const lagMean = fires.length > 0 ? lagSum / fires.length : 0;
  let lagVar = 0;
  for (const f of fires) {
    const lag = (f.now - f.sim) * 1000;
    lagVar += (lag - lagMean) * (lag - lagMean);
  }
  lagVar = fires.length > 0 ? lagVar / fires.length : 0;

  const driftSlope =
    fires.length >= 2
      ? ((fires[fires.length - 1].now - fires[fires.length - 1].sim) - (fires[0].now - fires[0].sim)) /
        Math.max(fires[fires.length - 1].now - fires[0].now, 1e-6)
      : 0;

  return {
    totalFires: fires.length,
    perObject,
    deliveryLagMs: {
      mean: lagMean,
      std: Math.sqrt(lagVar),
      min: fires.length > 0 ? lagMin : 0,
      max: fires.length > 0 ? lagMax : 0,
    },
    anchorDriftMsPerSec: driftSlope * 1000,
    emitInterval: intervalStats(emits),
  };
}

export function resetOnsetDiag(): void {
  fires.length = 0;
  emits.length = 0;
}
